import { useEffect, useMemo, useRef, useState } from 'react';
import { Film, Loader2, Play, Square, X } from 'lucide-react';
import clsx from 'clsx';
import {
  DEFAULT_ANIMATION,
  frameCountFor,
  supportsPingPong,
  sweepDimensions,
  type AnimationMode,
  type AnimationSettings,
  type EasingId,
} from '../../core/export/animate';
import {
  availableCodecs,
  estimateMemory,
  exportAnimation,
  type AnimationFormat,
  type VideoCodec,
} from '../../core/export/animationExport';
import { downloadBlob, formatFilename } from '../../core/export/download';
import { effectiveRange } from '../../core/params/bulk';
import { TEXT_TECHNIQUE_ID } from '../../store/layer';
import { useStudio } from '../../store/studioStore';
import { getTechnique } from '../../techniques/registry';
import { Row, Segmented, Select, Toggle } from '../controls/Basics';
import { Slider } from '../controls/Slider';

const MODE_HINTS: Record<AnimationMode, string> = {
  build: 'Der Aufbau der Technik selbst — Partikel für Partikel, Ast für Ast. Wird zuerst vermessen, damit die Frames gleichmässig über das Wachstum verteilt sind.',
  live: 'Für Techniken, die nie fertig werden: Boids, Fluid, Physarum. Eine Simulation, pro Frame ein Zeitschritt.',
  sweep: 'Ein Parameter fährt von A nach B. Als einziger Modus schleifenfähig — hin und zurück ergibt einen nahtlosen Loop.',
  seed: 'Jedes Frame ein anderer Seed. Das flackernde „Kochen“, wie bei gezeichnetem Film.',
};

/** Memory above which the buffered formats become a genuine risk to the tab. */
const MEMORY_WARN = 700 * 1024 * 1024;

export function AnimateDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const doc = useStudio((s) => s.doc);
  const font = useStudio((s) => s.font);

  // Animation is a different resolution question from a still image: every frame is held
  // in memory at once, so a 1080×1350 loop is most of a gigabyte before anything is
  // encoded. The dialog opens at whatever scale puts the long edge near 720 px — a
  // sensible size for a GIF or a web video — and the slider goes up from there.
  const [settings, setSettings] = useState<AnimationSettings>(() => ({
    ...DEFAULT_ANIMATION,
    scale: Math.min(1, Math.max(0.25, Math.round((720 / Math.max(doc.canvas.width, doc.canvas.height)) * 20) / 20)),
  }));
  const [format, setFormat] = useState<AnimationFormat>('gif');
  const [gifColors, setGifColors] = useState(128);
  const [gifDither, setGifDither] = useState(true);
  const [bitrate, setBitrate] = useState(12);
  const [template, setTemplate] = useState('{word}_{technique}_{mode}');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);

  const codecs = useMemo(() => availableCodecs(), []);
  const [codecId, setCodecId] = useState(() => codecs[0]?.id ?? '');
  const codec: VideoCodec | undefined = codecs.find((c) => c.id === codecId) ?? codecs[0];

  const patch = (next: Partial<AnimationSettings>): void => setSettings((s) => ({ ...s, ...next }));

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  // ── Derived facts about the composition ──────────────────────────────────
  const techniqueLayers = doc.layers.filter((l) => l.techniqueId !== TEXT_TECHNIQUE_ID && l.visible);
  const animatedLayers = techniqueLayers.filter((l) => getTechnique(l.techniqueId)?.isAnimated);

  const dimensions = useMemo(() => sweepDimensions(doc), [doc]);
  const sweepOptions = useMemo(
    () => dimensions.map((d) => ({ value: d.id, label: d.label, group: d.group })),
    [dimensions],
  );
  const sweepDim = dimensions.find((d) => d.id === settings.sweepDimensionId) ?? dimensions[0];

  // The from/to sliders show real parameter values, not abstract 0–1 positions.
  const sweepParam =
    sweepDim && (sweepDim.param.kind === 'float' || sweepDim.param.kind === 'int') ? sweepDim.param : null;
  const sweepRange: [number, number] = sweepParam ? effectiveRange(sweepParam, sweepDim?.spec) : [0, 1];
  const unitToValue = (u: number): number => sweepRange[0] + (sweepRange[1] - sweepRange[0]) * u;
  const valueToUnit = (v: number): number =>
    sweepRange[1] === sweepRange[0] ? 0 : (v - sweepRange[0]) / (sweepRange[1] - sweepRange[0]);

  const frames = frameCountFor(settings);
  const width = Math.round(doc.canvas.width * settings.scale);
  const height = Math.round(doc.canvas.height * settings.scale);
  const memory = estimateMemory(settings, doc, format);
  const seconds = frames / settings.fps;

  const tokens = {
    word: String(doc.textValues.text ?? 'text').split('\n')[0].slice(0, 24) || 'text',
    technique: techniqueLayers[0]?.techniqueId ?? 'text',
    mode: settings.mode,
    seed: doc.seed,
    size: `${width}x${height}`,
    fps: settings.fps,
  };

  const extension = format === 'gif' ? 'gif' : format === 'pngzip' ? 'zip' : (codec?.extension ?? 'webm');
  const filename = `${formatFilename(template, tokens)}.${extension}`;

  const blockers: string[] = [];
  if (!font) blockers.push('Die Schrift wird noch geladen.');
  if (techniqueLayers.length === 0) blockers.push('Ohne sichtbaren Technik-Layer gibt es nichts zu animieren.');
  if (settings.mode === 'sweep' && !sweepDim) blockers.push('Kein Parameter zum Fahren gefunden.');
  if (format === 'video' && !codec) blockers.push('Dieser Browser kann kein Video aufzeichnen.');

  const run = async (): Promise<void> => {
    if (!font) return;
    const controller = new AbortController();
    abort.current = controller;
    setBusy(true);
    setError(null);
    setMessage(null);
    setNotes([]);
    setProgress(0);

    try {
      const result = await exportAnimation({
        doc,
        font,
        settings: settings.mode === 'sweep' && sweepDim ? { ...settings, sweepDimensionId: sweepDim.id } : settings,
        format,
        codec,
        bitrateMbps: bitrate,
        gifColors,
        gifDither,
        signal: controller.signal,
        onProgress: (p, label) => {
          setProgress(p);
          setPhase(label);
        },
      });
      downloadBlob(result.blob, filename);
      setMessage(
        `${filename} — ${result.frames} Frames, ${(result.blob.size / 1024 / 1024).toFixed(1)} MB, ` +
          `${(result.ms / 1000).toFixed(1)} s`,
      );
      setNotes(result.notes);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      abort.current = null;
      setBusy(false);
      setProgress(0);
      setPhase('');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-6"
      onClick={() => !busy && onClose()}
    >
      <div
        className="max-h-full w-[460px] overflow-y-auto rounded-xl border border-ink-700 bg-ink-900 shadow-2xl shadow-black/70"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-ink-800 bg-ink-900 px-4 py-3">
          <Film size={13} className="text-accent-500" />
          <span className="flex-1 text-[13px] font-medium text-ink-100">Animation exportieren</span>
          <button type="button" className="ghost-button h-6 w-6 p-0" onClick={onClose} disabled={busy}>
            <X size={13} />
          </button>
        </div>

        <div className="space-y-1 px-4 py-3">
          {/* ── What moves ─────────────────────────────────────────────── */}
          <Row label="Bewegung">
            <Segmented
              size="sm"
              value={settings.mode}
              onChange={(mode) => patch({ mode })}
              options={[
                { value: 'build', label: 'Aufbau' },
                { value: 'live', label: 'Simulation' },
                { value: 'sweep', label: 'Parameter' },
                { value: 'seed', label: 'Seed' },
              ]}
            />
          </Row>
          <p className="pb-1 pl-[100px] text-[10px] leading-relaxed text-ink-500">{MODE_HINTS[settings.mode]}</p>

          {settings.mode === 'live' && animatedLayers.length === 0 && (
            <p className="rounded-md border border-amber-900/50 bg-amber-950/40 px-2.5 py-2 text-[10px] leading-relaxed text-amber-200">
              Keiner der sichtbaren Layer ist eine laufende Simulation — im Simulationsmodus stehen sie nach ihrem
              letzten Schritt still. „Aufbau“ passt hier besser.
            </p>
          )}

          {settings.mode === 'sweep' && sweepDim && (
            <>
              <Row label="Parameter">
                <Select
                  value={sweepDim.id}
                  onChange={(id) => patch({ sweepDimensionId: id, sweepFrom: 0, sweepTo: 1 })}
                  options={sweepOptions}
                />
              </Row>
              <Slider
                label="Von"
                value={unitToValue(settings.sweepFrom)}
                onChange={(v) => patch({ sweepFrom: valueToUnit(v) })}
                min={sweepRange[0]}
                max={sweepRange[1]}
                step={(sweepRange[1] - sweepRange[0]) / 200}
              />
              <Slider
                label="Bis"
                value={unitToValue(settings.sweepTo)}
                onChange={(v) => patch({ sweepTo: valueToUnit(v) })}
                min={sweepRange[0]}
                max={sweepRange[1]}
                step={(sweepRange[1] - sweepRange[0]) / 200}
              />
            </>
          )}

          {/* ── Timing ─────────────────────────────────────────────────── */}
          <Slider
            label="Bildrate"
            value={settings.fps}
            onChange={(v) => patch({ fps: Math.round(v) })}
            min={6}
            max={60}
            step={1}
            defaultValue={30}
            unit="fps"
            precision={0}
          />
          <Slider
            label="Dauer"
            value={settings.duration}
            onChange={(v) => patch({ duration: v })}
            min={0.5}
            max={20}
            step={0.1}
            defaultValue={4}
            unit="s"
            precision={1}
            tooltip="Länge der Bewegung, ohne den gehaltenen Schluss."
          />
          {supportsPingPong(settings.mode) ? (
            <Toggle
              checked={settings.pingPong}
              onChange={(pingPong) => patch({ pingPong })}
              label="Hin und zurück (nahtlose Schleife)"
              tooltip="Der Parameter fährt zurück auf den Anfangswert, sodass das letzte Frame ans erste anschliesst."
            />
          ) : (
            <Slider
              label="Schluss halten"
              value={settings.holdSeconds}
              onChange={(v) => patch({ holdSeconds: v })}
              min={0}
              max={4}
              step={0.1}
              defaultValue={0.6}
              unit="s"
              precision={1}
              tooltip="Aufbau und Simulation laufen nur vorwärts. Statt einer Schleife hält das Ergebnis kurz an, bevor es von vorn beginnt."
            />
          )}
          <Row label="Verlauf">
            <Segmented
              size="sm"
              value={settings.easing}
              onChange={(easing: EasingId) => patch({ easing })}
              options={[
                { value: 'linear', label: 'Gleichmässig' },
                { value: 'easeOut', label: 'Auslaufend' },
                { value: 'easeInOut', label: 'Weich' },
              ]}
            />
          </Row>

          {/* ── Output ─────────────────────────────────────────────────── */}
          <div className="my-2 border-t border-ink-800" />

          <Row label="Format">
            <Segmented
              size="sm"
              value={format}
              onChange={setFormat}
              options={[
                { value: 'gif', label: 'GIF', title: 'Läuft überall, 256 Farben' },
                { value: 'video', label: 'Video', title: 'WebM oder MP4, je nach Browser' },
                { value: 'pngzip', label: 'PNG-Folge', title: 'Verlustfrei, für Schnittprogramme' },
              ]}
            />
          </Row>

          {format === 'video' && codecs.length > 1 && (
            <Row label="Container">
              <Select
                value={codecId}
                onChange={setCodecId}
                options={codecs.map((c) => ({ value: c.id, label: c.label }))}
              />
            </Row>
          )}
          {format === 'video' && (
            <Slider
              label="Bitrate"
              value={bitrate}
              onChange={setBitrate}
              min={2}
              max={40}
              step={1}
              defaultValue={12}
              unit="Mbit/s"
              precision={0}
            />
          )}
          {format === 'gif' && (
            <>
              <Slider
                label="Farben"
                value={gifColors}
                onChange={(v) => setGifColors(Math.round(v))}
                min={8}
                max={256}
                step={8}
                defaultValue={128}
                precision={0}
                tooltip="Weniger Farben ergeben eine kleinere Datei und sichtbare Stufen in Verläufen."
              />
              <Toggle
                checked={gifDither}
                onChange={setGifDither}
                label="Rastern"
                tooltip="Bricht die Stufen in Verläufen auf — die Stärke richtet sich nach dem tatsächlichen Fehler der Palette. Deterministisch, damit es zwischen Frames nicht kribbelt. Kostet etwa das Doppelte an Dateigrösse."
              />
            </>
          )}

          <Slider
            label="Auflösung"
            value={settings.scale}
            onChange={(v) => patch({ scale: v })}
            min={0.25}
            max={2}
            step={0.05}
            defaultValue={1}
            unit="×"
            precision={2}
            tooltip="Vielfaches der Dokumentgrösse. Längenparameter skalieren automatisch mit."
          />

          <Row label="Dateiname">
            <input
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              spellCheck={false}
              className="field-input font-mono text-[11px]"
            />
          </Row>

          {/* ── Summary ────────────────────────────────────────────────── */}
          <div className="rounded-md bg-ink-850 px-2.5 py-2 font-mono text-[10px] leading-relaxed text-ink-400">
            <div className="truncate text-ink-200">{filename}</div>
            <div className="text-ink-600">
              {frames} Frames · {seconds.toFixed(1)} s · {width} × {height} px
            </div>
            {memory > 0 && (
              <div className={clsx(memory > MEMORY_WARN ? 'text-amber-400' : 'text-ink-600')}>
                {(memory / 1024 / 1024).toFixed(0)} MB Frames im Speicher
                {memory > MEMORY_WARN && ' — Auflösung oder Dauer reduzieren'}
              </div>
            )}
            <div className="text-ink-600">Platzhalter: {'{word} {technique} {mode} {seed} {size} {fps}'}</div>
          </div>

          {blockers.length > 0 && (
            <p className="rounded-md border border-amber-900/50 bg-amber-950/40 px-2.5 py-2 text-[10px] leading-relaxed text-amber-200">
              {blockers.join(' ')}
            </p>
          )}
          {message && (
            <p className="rounded-md bg-ink-850 px-2.5 py-2 font-mono text-[10px] text-accent-500">{message}</p>
          )}
          {notes.map((note) => (
            <p key={note} className="rounded-md bg-ink-850 px-2.5 py-2 text-[10px] leading-relaxed text-ink-400">
              {note}
            </p>
          ))}
          {error && (
            <p className="rounded-md border border-red-900/50 bg-red-950/40 px-2.5 py-2 text-[10px] text-red-200">
              {error}
            </p>
          )}
        </div>

        <div className="sticky bottom-0 flex items-center gap-2 border-t border-ink-800 bg-ink-900 px-4 py-3">
          {busy ? (
            <>
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-ink-800">
                <div
                  className="h-full bg-accent-500 transition-[width] duration-200"
                  style={{ width: `${Math.round(progress * 100)}%` }}
                />
              </div>
              <span className="w-[130px] shrink-0 truncate font-mono text-[10px] text-ink-500">{phase}</span>
              <button
                type="button"
                onClick={() => abort.current?.abort()}
                className="inline-flex items-center gap-1.5 rounded-md bg-ink-750 px-3 py-1.5 text-[12px] text-ink-200 hover:bg-ink-700"
              >
                <Square size={11} />
                Abbrechen
              </button>
            </>
          ) : (
            <>
              <span className="flex-1 text-[10px] leading-relaxed text-ink-500">
                {format === 'video'
                  ? 'Frames werden zuerst gerendert, dann in Echtzeit aufgezeichnet.'
                  : 'Jedes Frame wird in voller Qualität gerendert, unabhängig von der Vorschau.'}
              </span>
              <button
                type="button"
                disabled={blockers.length > 0}
                onClick={() => void run()}
                className={clsx(
                  'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors',
                  blockers.length > 0
                    ? 'bg-ink-750 text-ink-500'
                    : 'bg-accent-500 text-ink-950 hover:bg-accent-400',
                )}
              >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
                Rendern
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
